// ============================================================
// MySQLExamination.js
// ============================================================
// Junior-dev notes:
// - Definitions for 'current' and 'handleChoose' are placed at the top level of the component.
// - Anti-cheat uses useEffect to attach/detach listeners based on the 'started' state.
// - Progress bar uses (activeIndex / totalQuestions) * 100 for accuracy.
// ============================================================

import React, { useCallback, useEffect, useRef, useState } from "react";
import {
    Box,
    Container,
    Typography,
    Divider,
    Paper,
    Chip,
    Stack,
    Button,
    IconButton,
    LinearProgress,
    Tooltip,
    Switch,
    FormControlLabel,
    Alert,
    Snackbar,
    Grid,
    RadioGroup,
    FormControl,
    FormControlLabel as MUIFormControlLabel,
    Radio,
    TextField,
    MenuItem,
} from "@mui/material";

/* MUI Icons */
import SchoolIcon from "@mui/icons-material/School";
import TimerIcon from "@mui/icons-material/Timer";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";

/* React Icons */
import { FaVolumeUp, FaVolumeMute, FaShieldAlt, FaLightbulb } from "react-icons/fa";

/* ── Brand tokens ────────────────────────────────────────── */
const BRAND = {
    bg: "#100720",
    surface: "rgba(255,255,255,0.04)",
    border: "rgba(218,165,32,0.25)",
    gold: "#DAA520",
    goldBright: "#FFD700",
    white: "#FFFFFF",
    muted: "rgba(255,255,255,0.70)",
    success: "#2ecc71",
    error: "#e74c3c",
    inputBg: "rgba(255,255,255,0.05)",
};

const QUIZ_CONFIG = {
    totalQuestions: 30,
    durationSeconds: 15 * 60,
    maxViolations: 3,
    storageKeyMastery: "mysql_quiz_mastery_v2",
};

/* ── Static Questions ────────────────────────────────────── */
const QUESTION_BANK = [
    { id: "Q1", topic: "Foundations", difficulty: 1, question: "What does SQL stand for?", options: ["Structured Query Language", "Standard Query List", "System Question Language", "Structured Queue Logic"], answerIndex: 0, explanation: "SQL stands for Structured Query Language." },
    { id: "Q2", topic: "Data Types", difficulty: 1, question: "Which MySQL data type is best for storing currency values accurately?", options: ["FLOAT", "DOUBLE", "DECIMAL", "TEXT"], answerIndex: 2, explanation: "DECIMAL stores fixed-point numbers precisely." },
    { id: "Q3", topic: "Table Design", difficulty: 1, question: "What constraint ensures values in a column are not duplicated?", options: ["NOT NULL", "UNIQUE", "DEFAULT", "CHECK"], answerIndex: 1, explanation: "UNIQUE ensures all values in the column are distinct." },
    { id: "Q4", topic: "Keys & Relationships", difficulty: 1, question: "A FOREIGN KEY is used to:", options: ["Speed up queries", "Uniquely identify a row", "Link rows between tables", "Encrypt data"], answerIndex: 2, explanation: "Foreign keys link rows between tables." },
    { id: "Q5", topic: "CRUD", difficulty: 1, question: "Which statement is used to add new rows into a table?", options: ["UPDATE", "INSERT", "ALTER", "MERGE"], answerIndex: 1, explanation: "INSERT adds new rows." },
    { id: "Q6", topic: "CRUD", difficulty: 1, question: "Which clause is critical to safely target rows in UPDATE/DELETE?", options: ["GROUP BY", "HAVING", "WHERE", "LIMIT"], answerIndex: 2, explanation: "WHERE limits which rows are affected." },
    { id: "Q7", topic: "Filtering", difficulty: 1, question: "Which operator is commonly used for pattern matching in SQL?", options: ["IN", "LIKE", "BETWEEN", "EXISTS"], answerIndex: 1, explanation: "LIKE is used with wildcards." },
    { id: "Q8", topic: "Sorting", difficulty: 1, question: "Which clause is used to sort query results?", options: ["ORDER BY", "GROUP BY", "SORT BY", "ARRANGE BY"], answerIndex: 0, explanation: "ORDER BY sorts results." },
    { id: "Q9", topic: "Functions", difficulty: 1, question: "Which function returns the number of rows in a result set?", options: ["SUM()", "COUNT()", "AVG()", "MAX()"], answerIndex: 1, explanation: "COUNT() returns the row count." },
    { id: "Q10", topic: "Functions", difficulty: 2, question: "Which clause filters aggregated results?", options: ["WHERE", "HAVING", "LIMIT", "DISTINCT"], answerIndex: 1, explanation: "HAVING filters after GROUP BY aggregation." },
    { id: "Q11", topic: "Joins", difficulty: 1, question: "Which JOIN returns only matching rows from both tables?", options: ["LEFT JOIN", "RIGHT JOIN", "INNER JOIN", "FULL JOIN"], answerIndex: 2, explanation: "INNER JOIN returns matching rows only." },
    { id: "Q12", topic: "Joins", difficulty: 1, question: "Which JOIN returns all rows from the left table?", options: ["LEFT JOIN", "INNER JOIN", "CROSS JOIN", "SELF JOIN"], answerIndex: 0, explanation: "LEFT JOIN returns all left-side rows." },
    { id: "Q13", topic: "Relationships", difficulty: 2, question: "Which structure models a many-to-many relationship?", options: ["Single table", "Junction (bridge) table", "View", "Trigger"], answerIndex: 1, explanation: "A junction table links two tables." },
    { id: "Q14", topic: "Subqueries", difficulty: 2, question: "A subquery is:", options: ["A query inside another", "A JOIN query", "A row update", "A query without WHERE"], answerIndex: 0, explanation: "Subqueries are nested statements." },
    { id: "Q15", topic: "Views", difficulty: 2, question: "A VIEW in MySQL is best described as:", options: ["A snapshot", "A virtual table", "A backup", "A user role"], answerIndex: 1, explanation: "A VIEW is a virtual table." },
    { id: "Q16", topic: "Indexing", difficulty: 2, question: "What is the primary purpose of an index?", options: ["Encrypt", "Speed up retrieval", "Store backups", "Prevent INSERTs"], answerIndex: 1, explanation: "Indexes speed up data retrieval." },
    { id: "Q17", topic: "Optimization", difficulty: 2, question: "Which statement shows the query execution plan?", options: ["DESCRIBE", "SHOW TABLES", "EXPLAIN", "CHECK TABLE"], answerIndex: 2, explanation: "EXPLAIN shows the plan." },
    { id: "Q18", topic: "Indexing", difficulty: 3, question: "What pattern prevents index usage?", options: ["WHERE email = 'x'", "WHERE LOWER(email) = 'x'", "WHERE email IN ('x')", "WHERE email LIKE 'x%'"], answerIndex: 1, explanation: "Functions on columns can break index usage." },
    { id: "Q19", topic: "Transactions", difficulty: 2, question: "Which command saves transaction changes?", options: ["ROLLBACK", "COMMIT", "SAVEPOINT", "FLUSH"], answerIndex: 1, explanation: "COMMIT saves changes." },
    { id: "Q20", topic: "Transactions", difficulty: 2, question: "Which ACID property ensures 'all-or-nothing'?", options: ["Consistency", "Atomicity", "Isolation", "Durability"], answerIndex: 1, explanation: "Atomicity ensures completion." },
    { id: "Q21", topic: "ACID", difficulty: 2, question: "Which ACID property ensures crash survival?", options: ["Consistency", "Atomicity", "Isolation", "Durability"], answerIndex: 3, explanation: "Durability persists data." },
    { id: "Q22", topic: "Transactions", difficulty: 3, question: "MySQL’s default isolation level is:", options: ["READ UNCOMMITTED", "READ COMMITTED", "REPEATABLE READ", "SERIALIZABLE"], answerIndex: 2, explanation: "Default is REPEATABLE READ." },
    { id: "Q23", topic: "Stored Programs", difficulty: 2, question: "A stored procedure is:", options: ["Backup", "Reusable SQL program", "Index type", "Permission"], answerIndex: 1, explanation: "Stored procedures are programs." },
    { id: "Q24", topic: "Triggers", difficulty: 2, question: "A trigger is executed:", options: ["Manually", "Automatically on events", "Only on SELECT", "At startup"], answerIndex: 1, explanation: "Triggers fire on DML events." },
    { id: "Q25", topic: "Triggers", difficulty: 3, question: "Which timing prevents invalid data?", options: ["AFTER INSERT", "AFTER UPDATE", "BEFORE INSERT", "AFTER DELETE"], answerIndex: 2, explanation: "BEFORE triggers validate early." },
    { id: "Q26", topic: "Security", difficulty: 2, question: "Which statement gives user permissions?", options: ["GRANT", "REVOKE", "DROP USER", "FLUSH LOGS"], answerIndex: 0, explanation: "GRANT assigns privileges." },
    { id: "Q27", topic: "Security", difficulty: 2, question: "Least privilege means:", options: ["Give all root", "Give only what is needed", "Disable auth", "Store passwords"], answerIndex: 1, explanation: "Least privilege limits access." },
    { id: "Q28", topic: "App Integration", difficulty: 2, question: "Best practice against SQL injection?", options: ["Concatenation", "Prepared statements", "Disable WHERE", "SELECT *"], answerIndex: 1, explanation: "Prepared statements stop injection." },
    { id: "Q29", topic: "App Integration", difficulty: 2, question: "Spring Data repositories provide:", options: ["UI", "Data access methods", "Backups", "Index creation"], answerIndex: 1, explanation: "Repositories handle persistence." },
    { id: "Q30", topic: "DBA Ops", difficulty: 2, question: "Common tool for logical backups?", options: ["mysqldump", "EXPLAIN", "GRANT", "JOIN"], answerIndex: 0, explanation: "mysqldump is the backup tool." },
];

export default function MySQLExamination() {
    const [voiceEnabled, setVoiceEnabled] = useState(true);
    const [started, setStarted] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [timeLeft, setTimeLeft] = useState(QUIZ_CONFIG.durationSeconds);
    const timerRef = useRef(null);

    const [formData, setFormData] = useState({
        fullName: "", track: "", date: new Date().toISOString().split("T")[0],
        time: "", location: "", attemptNumber: 1
    });

    const [violations, setViolations] = useState([]);
    const [questions, setQuestions] = useState([]);
    const [activeIndex, setActiveIndex] = useState(0);
    const [answers, setAnswers] = useState([]);
    const [results, setResults] = useState(null);
    const [snack, setSnack] = useState({ open: false, message: "", severity: "warning" });

    const current = questions[activeIndex];
    const progress = Math.round(((activeIndex + 1) / (questions.length || 1)) * 100);

    const speak = useCallback((text) => {
        if (!voiceEnabled || !window.speechSynthesis) return;
        window.speechSynthesis.cancel();
        window.speechSynthesis.speak(new SpeechSynthesisUtterance(text));
    }, [voiceEnabled]);

    const handleChoose = (idx) => {
        const next = [...answers];
        next[activeIndex] = idx;
        setAnswers(next);
        speak("Answer recorded.");
    };

    const addViolation = useCallback((type) => {
        setViolations((prev) => {
            const next = [...prev, { type, at: new Date().toLocaleTimeString() }];
            setSnack({ open: true, message: `Anti-Cheat Warning: ${type}`, severity: "error" });
            speak(`Warning. ${type} detected. This is violation ${next.length}.`);
            if (next.length >= QUIZ_CONFIG.maxViolations) {
                handleSubmit("Auto-submitted: Max Violations reached.");
            }
            return next;
        });
    }, [speak]);

    // ── Anti-Cheat Effect ──────────────────────────────────
    useEffect(() => {
        if (!started || submitted) return;

        const onVisibility = () => { if (document.visibilityState !== "visible") addViolation("Tab switched"); };
        const onBlur = () => addViolation("Window lost focus");

        document.addEventListener("visibilitychange", onVisibility);
        window.addEventListener("blur", onBlur);

        return () => {
            document.removeEventListener("visibilitychange", onVisibility);
            window.removeEventListener("blur", onBlur);
        };
    }, [started, submitted, addViolation]);

    const handleStart = async () => {
        const q = [...QUESTION_BANK].sort(() => 0.5 - Math.random()).slice(0, 30);
        setQuestions(q);
        setAnswers(new Array(q.length).fill(null));
        setStarted(true);
        speak(`Candidacy exam started for ${formData.fullName}.`);

        timerRef.current = setInterval(() => setTimeLeft((t) => t - 1), 1000);
        try { if (document.documentElement.requestFullscreen) await document.documentElement.requestFullscreen(); } catch {}
    };

    const handleSubmit = (reason = "Manual Submission") => {
        if (timerRef.current) clearInterval(timerRef.current);
        const score = questions.reduce((acc, q, i) => acc + (answers[i] === q.answerIndex ? 1 : 0), 0);
        setResults({
            percent: Math.round((score / questions.length) * 100),
            correct: score,
            total: questions.length,
            candidate: formData,
            violations,
            reason
        });
        setSubmitted(true);
        setStarted(false);
        if (document.fullscreenElement) document.exitFullscreen();
    };

    return (
        <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: 6, px: 4 }}>
            <Container maxWidth="lg">
                {/* Header */}
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 4 }}>
                    <Typography variant="h4" sx={{ color: BRAND.white, fontWeight: 900 }}>MySQL <span style={{ color: BRAND.gold }}>Candidacy</span></Typography>
                    <IconButton onClick={() => setVoiceEnabled(!voiceEnabled)} sx={{ border: `1px solid ${BRAND.border}`, bgcolor: BRAND.surface }}>
                        {voiceEnabled ? <FaVolumeUp color={BRAND.goldBright} /> : <FaVolumeMute color={BRAND.goldBright} />}
                    </IconButton>
                </Stack>

                {/* 1. Registration Form */}
                {!started && !submitted && (
                    <Paper sx={{ p: 4, bgcolor: BRAND.surface, border: `1px solid ${BRAND.border}`, borderRadius: 4 }}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 3 }}>
                            <AssignmentIndIcon sx={{ color: BRAND.gold }} />
                            <Typography variant="h6" sx={{ color: BRAND.white }}>Examination Entry Form</Typography>
                        </Stack>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <TextField fullWidth label="Full Name" value={formData.fullName} onChange={(e) => setFormData({...formData, fullName: e.target.value})} variant="filled" sx={{ bgcolor: BRAND.inputBg }} InputLabelProps={{ style: { color: BRAND.muted } }} inputProps={{ style: { color: BRAND.white } }} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField select fullWidth label="Track" value={formData.track} onChange={(e) => setFormData({...formData, track: e.target.value})} variant="filled" sx={{ bgcolor: BRAND.inputBg }} InputLabelProps={{ style: { color: BRAND.muted } }} inputProps={{ style: { color: BRAND.white } }}>
                                    <MenuItem value="Database Admin">Database Administrator</MenuItem>
                                    <MenuItem value="Backend Dev">Backend Developer</MenuItem>
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField fullWidth label="Location" value={formData.location} onChange={(e) => setFormData({...formData, location: e.target.value})} variant="filled" sx={{ bgcolor: BRAND.inputBg }} InputLabelProps={{ style: { color: BRAND.muted } }} inputProps={{ style: { color: BRAND.white } }} />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField fullWidth type="date" label="Date" value={formData.date} variant="filled" sx={{ bgcolor: BRAND.inputBg }} InputLabelProps={{ shrink: true, style: { color: BRAND.muted } }} inputProps={{ style: { color: BRAND.white } }} />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField fullWidth type="number" label="Attempt #" value={formData.attemptNumber} onChange={(e) => setFormData({...formData, attemptNumber: e.target.value})} variant="filled" sx={{ bgcolor: BRAND.inputBg }} InputLabelProps={{ style: { color: BRAND.muted } }} inputProps={{ style: { color: BRAND.white } }} />
                            </Grid>
                        </Grid>
                        <Button fullWidth disabled={!formData.fullName || !formData.track} onClick={handleStart} variant="contained" sx={{ mt: 3, py: 2, bgcolor: BRAND.gold, color: BRAND.bg, fontWeight: 900 }}>Enter Examination Hall</Button>
                    </Paper>
                )}

                {/* 2. Quiz and Progress Bar */}
                {started && current && (
                    <Stack spacing={3}>
                        <Paper sx={{ p: 2, bgcolor: BRAND.surface, border: `1px solid ${BRAND.border}` }}>
                            <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                                <Typography sx={{ color: BRAND.gold }}>Candidate: {formData.fullName}</Typography>
                                <Typography sx={{ color: BRAND.white, fontWeight: 700 }}>Time: {Math.floor(timeLeft / 60)}:{String(timeLeft % 60).padStart(2, '0')}</Typography>
                            </Stack>
                            <LinearProgress variant="determinate" value={progress} sx={{ height: 10, borderRadius: 5, bgcolor: "rgba(255,255,255,0.05)", "& .MuiLinearProgress-bar": { bgcolor: BRAND.gold } }} />
                            <Typography variant="caption" sx={{ color: BRAND.muted, mt: 1, display: 'block' }}>Progress: {progress}% (Question {activeIndex + 1}/30)</Typography>
                        </Paper>

                        <Paper sx={{ p: 4, bgcolor: BRAND.surface, border: `1px solid ${BRAND.border}`, borderRadius: 4 }}>
                            <Typography variant="h6" sx={{ color: BRAND.white, mb: 3 }}>{current.question}</Typography>
                            <RadioGroup value={answers[activeIndex] ?? ""} onChange={(e) => handleChoose(Number(e.target.value))}>
                                {current.options.map((opt, i) => (
                                    <MUIFormControlLabel key={i} value={i} control={<Radio sx={{ color: BRAND.gold }} />} label={<Typography sx={{ color: BRAND.white }}>{opt}</Typography>} sx={{ p: 1.5, my: 0.5, border: `1px solid ${BRAND.border}`, borderRadius: 2, "&:hover": { bgcolor: "rgba(255,255,255,0.02)" } }} />
                                ))}
                            </RadioGroup>
                            <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
                                <Button variant="outlined" onClick={() => setActiveIndex(prev => Math.max(0, prev - 1))} sx={{ color: BRAND.gold, borderColor: BRAND.gold }}>Previous</Button>
                                {activeIndex < 29 ? (
                                    <Button variant="contained" onClick={() => setActiveIndex(prev => prev + 1)} sx={{ bgcolor: BRAND.gold, color: BRAND.bg, fontWeight: 700 }}>Next Question</Button>
                                ) : (
                                    <Button variant="contained" onClick={() => handleSubmit()} sx={{ bgcolor: BRAND.success, color: BRAND.bg, fontWeight: 900 }}>Finalize Submission</Button>
                                )}
                            </Stack>
                        </Paper>
                        <Alert icon={<FaShieldAlt color={BRAND.gold} />} sx={{ bgcolor: "transparent", color: BRAND.muted, border: `1px solid ${BRAND.border}` }}>
                            Anti-cheat active. Violations: {violations.length}/{QUIZ_CONFIG.maxViolations}
                        </Alert>
                    </Stack>
                )}

                {/* 3. Results */}
                {submitted && results && (
                    <Paper sx={{ p: 4, bgcolor: BRAND.surface, border: `1px solid ${BRAND.border}`, borderRadius: 4, textAlign: 'center' }}>
                        <Typography variant="h5" sx={{ color: BRAND.gold, mb: 2 }}>Exam Transcript Generated</Typography>
                        <Typography variant="h2" sx={{ color: BRAND.goldBright, fontWeight: 900 }}>{results.percent}%</Typography>
                        <Typography sx={{ color: BRAND.white, mb: 4 }}>{results.correct} out of {results.total} correct</Typography>
                        <Grid container spacing={2} sx={{ textAlign: 'left', bgcolor: 'rgba(0,0,0,0.2)', p: 2, borderRadius: 2 }}>
                            <Grid item xs={6} sx={{ color: BRAND.muted }}>Name: <span style={{color: BRAND.white}}>{results.candidate.fullName}</span></Grid>
                            <Grid item xs={6} sx={{ color: BRAND.muted }}>Attempt: <span style={{color: BRAND.white}}>{results.candidate.attemptNumber}</span></Grid>
                            <Grid item xs={12} sx={{ color: BRAND.muted }}>Submission: <span style={{color: BRAND.error}}>{results.reason}</span></Grid>
                        </Grid>
                        <Button fullWidth variant="contained" sx={{ mt: 4, bgcolor: BRAND.gold }} onClick={() => window.location.reload()}>Finish</Button>
                    </Paper>
                )}

                <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack({...snack, open: false})}>
                    <Alert severity={snack.severity} variant="filled">{snack.message}</Alert>
                </Snackbar>
            </Container>
        </Box>
    );
}
